import { useState, useEffect, useRef } from 'react';

/**
 * DestructiveActionConfirm Modal
 * 
 * Confirmation dialog for dangerous actions requiring a reason.
 * 
 * @param {object} props
 * @param {boolean} props.open - Whether modal is open
 * @param {function} props.onClose - Callback when modal is closed (cancel or outside click)
 * @param {function} props.onConfirm - Callback when confirmed (receives reason string)
 * @param {string} props.title - Modal title
 * @param {string} props.description - Modal description/context
 * @param {string} props.confirmLabel - Label for confirm button (default: "Confirm")
 * @param {string} props.reasonLabel - Label for reason field (default: "Reason")
 * @param {string} props.reasonPlaceholder - Placeholder for reason input
 * @param {boolean} props.loading - Whether action is in progress
 * @param {string|null} props.error - Error message to display
 */
export default function DestructiveActionConfirm({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  reasonLabel = 'Reason',
  reasonPlaceholder = 'Enter a reason for this action...',
  loading = false,
  error = null,
}) {
  const [reason, setReason] = useState('');
  const reasonInputRef = useRef(null);
  const modalRef = useRef(null);
  const previousActiveElementRef = useRef(null);

  // Focus trap: keep focus within modal
  useEffect(() => {
    if (!open) return;

    // Store the previously focused element
    previousActiveElementRef.current = document.activeElement;

    // Focus reason input when modal opens
    setTimeout(() => {
      reasonInputRef.current?.focus();
    }, 100);

    // Focus trap: handle Tab key
    const handleTab = (e) => {
      if (!modalRef.current) return;

      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    // Handle Escape key
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !loading) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleTab);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleTab);
      document.removeEventListener('keydown', handleEscape);
      // Restore focus to previously focused element
      previousActiveElementRef.current?.focus();
    };
  }, [open, loading, onClose]);

  // Reset reason when modal closes
  useEffect(() => {
    if (!open) {
      setReason('');
    }
  }, [open]);

  if (!open) {
    return null;
  }

  const handleConfirm = () => {
    if (reason.trim()) {
      onConfirm(reason.trim());
    }
  };

  const canConfirm = reason.trim().length > 0 && !loading;

  const modalId = `modal-${title.replace(/\s+/g, '-').toLowerCase()}`;
  const titleId = `${modalId}-title`;
  const descriptionId = `${modalId}-description`;

  return (
    <div
      style={styles.overlay}
      onClick={onClose}
      role="presentation"
      aria-hidden={!open}
    >
      <div
        ref={modalRef}
        style={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex={-1}
      >
        <div style={styles.header}>
          <h2 id={titleId} style={styles.title}>{title}</h2>
          {!loading && (
            <button
              onClick={onClose}
              style={styles.closeButton}
              aria-label="Close dialog"
              type="button"
            >
              ×
            </button>
          )}
        </div>

        <div style={styles.content}>
          {description && (
            <p id={descriptionId} style={styles.description}>{description}</p>
          )}

          <div style={styles.formGroup}>
            <label htmlFor={`${modalId}-reason`} style={styles.label}>
              {reasonLabel} <span style={styles.required} aria-label="required">*</span>
            </label>
            <textarea
              id={`${modalId}-reason`}
              ref={reasonInputRef}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={reasonPlaceholder}
              style={styles.textarea}
              rows={4}
              disabled={loading}
              aria-required="true"
              aria-describedby={`${modalId}-reason-help`}
            />
            <p id={`${modalId}-reason-help`} style={styles.helpText}>
              A reason is required for this action. It will be recorded in the audit log.
            </p>
          </div>

          {error && (
            <div style={styles.error} role="alert" aria-live="polite">
              <p>{error}</p>
            </div>
          )}
        </div>

        <div style={styles.footer}>
          <button
            onClick={onClose}
            disabled={loading}
            style={styles.cancelButton}
            type="button"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canConfirm}
            style={{
              ...styles.confirmButton,
              ...(!canConfirm && styles.confirmButtonDisabled),
            }}
            type="button"
            aria-label={loading ? 'Processing...' : confirmLabel}
          >
            {loading ? 'Processing...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 'var(--z-modal)',
  },
  modal: {
    backgroundColor: 'var(--color-bg-primary)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--elevation-3)',
    width: '90%',
    maxWidth: '500px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden', // Prevent modal from scrolling, only content scrolls
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 'var(--spacing-4) var(--spacing-5)',
    borderBottom: '1px solid var(--color-border-light)',
    flexShrink: 0, // Header doesn't shrink
  },
  title: {
    margin: 0,
    fontSize: 'var(--font-size-xl)',
    fontWeight: 'var(--font-weight-semibold)',
    lineHeight: 'var(--line-height-tight-medium)',
    letterSpacing: 'var(--letter-spacing-tight-medium)',
    color: 'var(--color-text-primary)',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: 'var(--font-size-xl)',
    color: 'var(--color-text-secondary)',
    cursor: 'pointer',
    padding: 'var(--spacing-1) var(--spacing-2)',
    lineHeight: 1,
    outline: 'none',
    borderRadius: 'var(--radius-sm)',
    transition: 'background-color var(--transition-fast)',
  },
  closeButtonFocus: {
    outline: 'var(--focus-outline-width) var(--focus-outline-style) var(--focus-outline-color)',
    outlineOffset: 'var(--focus-outline-offset)',
  },
  content: {
    padding: 'var(--spacing-5)',
    flex: 1,
    overflowY: 'auto',
    minHeight: 0, // Allow flex item to shrink below content size
  },
  description: {
    marginTop: 0,
    marginBottom: 'var(--spacing-4)',
    color: 'var(--color-text-secondary)',
    fontSize: 'var(--font-size-base)',
    fontWeight: 'var(--font-weight-normal)',
    lineHeight: 'var(--line-height-normal)',
  },
  formGroup: {
    marginBottom: 'var(--spacing-4)',
  },
  label: {
    display: 'block',
    marginBottom: 'var(--spacing-2)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-primary)',
    fontSize: 'var(--font-size-sm)',
  },
  required: {
    color: 'var(--color-status-error-base)',
  },
  textarea: {
    width: '100%',
    padding: 'var(--spacing-3) var(--spacing-4)',
    border: '1px solid var(--color-border-medium)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    fontFamily: 'var(--font-family)',
    resize: 'vertical',
    minHeight: '100px',
    backgroundColor: 'var(--color-bg-primary)',
    color: 'var(--color-text-primary)',
    lineHeight: 'var(--line-height-normal)',
    transition: 'border-color var(--transition-fast), box-shadow var(--transition-fast)',
  },
  helpText: {
    marginTop: 'var(--spacing-2)',
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-tertiary)',
    lineHeight: 'var(--line-height-compact)',
  },
  error: {
    marginTop: 'var(--spacing-4)',
    padding: 'var(--spacing-3)',
    backgroundColor: 'var(--color-status-error-bg)',
    color: 'var(--color-status-error-text)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    border: '1px solid var(--color-status-error-base)',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 'var(--spacing-2)',
    padding: 'var(--spacing-4) var(--spacing-5)',
    borderTop: '1px solid var(--color-border-light)',
    flexShrink: 0, // Footer doesn't shrink, stays visible
    backgroundColor: 'var(--color-bg-primary)', // Ensure footer has background for sticky effect
  },
  cancelButton: {
    padding: 'var(--spacing-2) var(--spacing-4)',
    border: '1px solid var(--color-border-medium)',
    borderRadius: 'var(--radius-md)',
    backgroundColor: 'var(--color-bg-primary)',
    color: 'var(--color-text-primary)',
    cursor: 'pointer',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    outline: 'none',
    transition: 'background-color var(--transition-fast), border-color var(--transition-fast)',
  },
  cancelButtonFocus: {
    outline: 'var(--focus-outline-width) var(--focus-outline-style) var(--focus-outline-color)',
    outlineOffset: 'var(--focus-outline-offset)',
  },
  confirmButton: {
    padding: 'var(--spacing-2) var(--spacing-4)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    backgroundColor: 'var(--color-status-error-base)',
    color: 'var(--color-text-inverse)',
    cursor: 'pointer',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    outline: 'none',
    transition: 'background-color var(--transition-fast), opacity var(--transition-fast)',
  },
  confirmButtonFocus: {
    outline: 'var(--focus-outline-width) var(--focus-outline-style) var(--color-text-inverse)',
    outlineOffset: 'var(--focus-outline-offset)',
  },
  confirmButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    backgroundColor: '#9ca3af',
  },
};

